import { useState } from "react";
import { ArrowLeft, Send, DollarSign, TrendingUp, Ban, BarChart2, Bot, ChevronRight, ChevronDown } from "lucide-react";
import { fmtCurrencyBR } from "../../../utils/formatBrl";
import { fmtDateTimezone } from "../../utils/dateUtils/fmtDate";

function StatBadge({ icon: Icon, label, value, sub, color = "text-white" }) {
    return (
        <div className="flex flex-col gap-1 p-3 rounded-xl border border-white/[0.06] bg-white/[0.02]">
            <div className="flex items-center gap-1.5 text-white/40 text-xs">
                <Icon size={12} />
                {label}
            </div>
            <span className={`text-lg font-semibold ${color}`}>{value}</span>
            {sub && <span className="text-xs text-white/30">{sub}</span>}
        </div>
    );
}

function StatusCell({ status }) {
    if (status === 'concluído') return <span className="text-green-400 text-xs font-medium">Concluído</span>;
    if (status === 'running' || status === 'disparando...')
        return <span className="text-blue-400 text-xs font-medium">Em andamento</span>;
    if (status === 'interrompido') return <span className="text-orange-400 text-xs font-medium">Interrompido</span>;
    if (status === 'inativo') return <span className="text-white/40 text-xs font-medium">Inativo</span>;
    if (status === 'erro') return <span className="text-red-400 text-xs font-medium">Erro</span>;
    return <span className="text-white/40 text-xs">{status}</span>;
}

// Barra visual enviados (verde) vs bloqueados (vermelho). Substitui os números
// crus — a proporção é lida de relance. `compact` reduz altura para as sub-linhas.
function ProgressBar({ sent = 0, blocked = 0, compact = false }) {
    const total = sent + blocked;
    const sentPct = total > 0 ? (sent / total) * 100 : 0;
    const blockedPct = total > 0 ? (blocked / total) * 100 : 0;
    const h = compact ? "h-1.5" : "h-2";
    return (
        <div className={`flex flex-col gap-1 w-full ${compact ? "max-w-[160px]" : "max-w-[130px]"}`}>
            <div className={`flex ${h} w-full rounded-full overflow-hidden bg-white/[0.06]`}>
                <div className="bg-green-500/80 h-full" style={{ width: `${sentPct}%` }} />
                <div className="bg-red-500/70 h-full" style={{ width: `${blockedPct}%` }} />
            </div>
            <div className="flex items-center gap-3 text-[11px] leading-none">
                <span className="text-green-400">{sent} enviados</span>
                <span className="text-red-400">{blocked} bloq.</span>
            </div>
        </div>
    );
}

function BotBreakdown({ bots }) {
    if (!bots || bots.length === 0) {
        return (
            <div className="px-4 py-3 text-xs text-white/40">
                Sem detalhamento por bot para esta execução.
            </div>
        );
    }
    return (
        <div className="px-4 py-3 space-y-2 bg-white/[0.015]">
            {bots.map((b) => (
                <div key={b.bot_id} className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-2 min-w-0">
                        <Bot size={13} className="text-[var(--purple-light)] shrink-0" />
                        <span className="text-sm text-white/70 truncate">
                            {b.bot_username ? `@${String(b.bot_username).replace(/^@/, "")}` : `Bot #${b.bot_id}`}
                        </span>
                        <StatusCell status={b.status} />
                    </div>
                    <ProgressBar sent={b.count_user_sent} blocked={b.count_user_blocked} compact />
                </div>
            ))}
        </div>
    );
}

function RunRow({ run, expanded, onToggle }) {
    const conv = run.payments_generated > 0
        ? ((run.payments_paid / run.payments_generated) * 100).toFixed(1) + '%'
        : '0.0%';
    const hasBots = (run.bots?.length ?? 0) > 0;
    return (
        <>
            <tr className={hasBots ? "cursor-pointer" : ""} onClick={hasBots ? onToggle : undefined}>
                <td className="w-[50px] font-semibold text-white/70">
                    <div className="flex items-center gap-1">
                        {hasBots && (expanded
                            ? <ChevronDown size={14} className="text-white/40" />
                            : <ChevronRight size={14} className="text-white/40" />)}
                        #{run.run_number}
                    </div>
                </td>
                <td className="text-sm text-white/60 w-[160px]">{run.started_at ? fmtDateTimezone(run.started_at) : '—'}</td>
                <td><StatusCell status={run.status} /></td>
                <td><ProgressBar sent={run.count_user_sent ?? 0} blocked={run.count_user_blocked ?? 0} /></td>
                <td>{run.payments_generated ?? 0}</td>
                <td>{run.payments_paid ?? 0}</td>
                <td>{fmtCurrencyBR.format(run.total_value ?? 0)}</td>
                <td>{conv}</td>
            </tr>
            {expanded && hasBots && (
                <tr>
                    <td colSpan={8} className="!p-0">
                        <BotBreakdown bots={run.bots} />
                    </td>
                </tr>
            )}
        </>
    );
}

export default function DisparoStats({ disparo, runs = [], onBack }) {
    const [expanded, setExpanded] = useState(() => new Set());
    const toggle = (id) => setExpanded((prev) => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
    });

    const totalSent      = runs.reduce((s, r) => s + (r.count_user_sent ?? 0), 0);
    const totalBlocked   = runs.reduce((s, r) => s + (r.count_user_blocked ?? 0), 0);
    const totalRevenue   = runs.reduce((s, r) => s + (r.total_value ?? 0), 0);
    const payGenerated   = runs.reduce((s, r) => s + (r.payments_generated ?? 0), 0);
    const payPaid        = runs.reduce((s, r) => s + (r.payments_paid ?? 0), 0);
    const conversion     = payGenerated > 0 ? ((payPaid / payGenerated) * 100).toFixed(1) + '%' : '0.0%';

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center gap-3">
                <button
                    onClick={onBack}
                    className="flex items-center gap-2 text-white/50 hover:text-white text-sm transition-all cursor-pointer"
                >
                    <ArrowLeft size={16} />
                    Voltar
                </button>
            </div>

            <div>
                <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                    <BarChart2 size={20} className="text-[var(--purple)]" />
                    Histórico — <span className="text-white/70 truncate max-w-xs">{disparo?.name}</span>
                </h2>
                <p className="text-white/40 text-sm mt-1">
                    Público-alvo: {disparo?.target_audience} · {runs.length} execuç{runs.length === 1 ? "ão" : "ões"}
                </p>
            </div>

            {/* Summary cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatBadge
                    icon={Send}
                    label="Enviados"
                    value={totalSent}
                    sub={`${totalSent} leads no total`}
                    color="text-white"
                />
                <StatBadge
                    icon={DollarSign}
                    label="Receita Total"
                    value={fmtCurrencyBR.format(totalRevenue)}
                    color="text-emerald-400"
                />
                <StatBadge
                    icon={TrendingUp}
                    label="Gerados / Pagos"
                    value={`${payGenerated} / ${payPaid}`}
                    sub={`Conversão: ${conversion}`}
                    color="text-[var(--purple-light)]"
                />
                <StatBadge
                    icon={Ban}
                    label="Bloq / Falhas"
                    value={`${totalBlocked} / 0`}
                    color="text-red-400"
                />
            </div>

            {/* Runs list */}
            {runs.length === 0 ? (
                <div className="empty-state py-12">
                    <Send />
                    <h4 className="mt-3">Nenhuma execução registrada</h4>
                    <p>Este disparo ainda não foi enviado.</p>
                </div>
            ) : (
                <>
                    <h3 className="text-sm font-semibold text-white/50 uppercase tracking-widest">Histórico de Disparos</h3>

                    {/* Desktop */}
                    <div className="sx-desktop">
                        <table className="sx-table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Data / Hora</th>
                                    <th>Status</th>
                                    <th>Progresso</th>
                                    <th>Gerados</th>
                                    <th>Pagos</th>
                                    <th>Receita</th>
                                    <th>Conversão</th>
                                </tr>
                            </thead>
                            <tbody>
                                {runs.map((r, i) => (
                                    <RunRow
                                        key={r.id ?? i}
                                        run={r}
                                        expanded={expanded.has(r.id ?? i)}
                                        onToggle={() => toggle(r.id ?? i)}
                                    />
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile */}
                    <div className="sx-mobile">
                        {runs.map((r, i) => {
                            const conv = r.payments_generated > 0
                                ? ((r.payments_paid / r.payments_generated) * 100).toFixed(1) + '%'
                                : '0.0%';
                            const id = r.id ?? i;
                            const isOpen = expanded.has(id);
                            const hasBots = (r.bots?.length ?? 0) > 0;
                            return (
                                <div key={id} className="sx-card">
                                    <div className="sx-card-header">
                                        <div className="sx-card-title-2">Envio #{r.run_number}</div>
                                        <StatusCell status={r.status} />
                                    </div>
                                    <div className="sx-card-row mt-3">
                                        <strong>Data:</strong>
                                        <span className="sx-card-meta">{r.started_at ? fmtDateTimezone(r.started_at) : '—'}</span>
                                    </div>
                                    <div className="mt-3">
                                        <strong className="text-xs text-white/50">Progresso:</strong>
                                        <div className="mt-1.5">
                                            <ProgressBar sent={r.count_user_sent ?? 0} blocked={r.count_user_blocked ?? 0} />
                                        </div>
                                    </div>
                                    <div className="sx-card-row mt-3">
                                        <strong>PIX Gerados/Pagos:</strong>
                                        <span className="sx-card-meta">{r.payments_paid ?? 0}/{r.payments_generated ?? 0}</span>
                                    </div>
                                    <div className="sx-card-row">
                                        <strong>Receita:</strong>
                                        <span className="sx-card-meta text-emerald-400">{fmtCurrencyBR.format(r.total_value ?? 0)}</span>
                                    </div>
                                    <div className="sx-card-row">
                                        <strong>Conversão:</strong>
                                        <span className="sx-card-meta">{conv}</span>
                                    </div>
                                    {hasBots && (
                                        <>
                                            <button
                                                onClick={() => toggle(id)}
                                                className="mt-3 flex items-center gap-1 text-xs text-[var(--purple-light)] cursor-pointer"
                                            >
                                                {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                                {isOpen ? "Ocultar" : "Ver"} progresso por bot ({r.bots.length})
                                            </button>
                                            {isOpen && (
                                                <div className="mt-2 -mx-4 border-t border-white/[0.06]">
                                                    <BotBreakdown bots={r.bots} />
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
}
